/**
 * Thermal printing.
 *
 * The API has NO rendered-receipt payload — no ESC/POS, no PDF, no HTML. It
 * issues an invoice number and returns totals; composing the paper is the
 * client's job. So the POS builds an 80 mm HTML document and prints it through
 * a hidden same-origin iframe.
 *
 * WHY AN IFRAME AND NOT `window.open`:
 *   - a kiosk browser in standalone PWA mode usually has popups disabled, and a
 *     blocked popup means a customer standing at the counter with no receipt;
 *   - `window.print()` on the main document would print the whole POS UI;
 *   - the iframe is same-origin, so `@media print` and the 80 mm page box
 *     apply exactly, and printing does not disturb the app's scroll position or
 *     React state.
 *
 * The 80 mm sizing (72 mm printable, 8 mm of margin) matches every common
 * thermal roll. `@page { size: 80mm auto }` lets the driver cut at content
 * length instead of ejecting an A4 sheet.
 */

const FRAME_ID = 'print-frame';

const BASE_STYLE = `
  @page { size: 80mm auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #000; }
  body {
    width: 80mm;
    padding: 3mm 4mm;
    /* A monospace-first stack keeps columns aligned on a fixed-pitch printer
       and still renders Vietnamese diacritics; Consolas/Menlo/DejaVu all do. */
    font-family: 'DejaVu Sans Mono', Consolas, Menlo, 'Courier New', monospace;
    font-size: 11px;
    line-height: 1.35;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  h1 { font-size: 15px; margin: 0 0 1mm; text-align: center; letter-spacing: .5px; }
  h2 { font-size: 12px; margin: 0 0 2mm; text-align: center; font-weight: 600; }
  .muted { color: #333; }
  .center { text-align: center; }
  .right { text-align: right; }
  .rule { border-top: 1px dashed #000; margin: 2mm 0; }
  table { width: 100%; border-collapse: collapse; }
  td, th { padding: 0.4mm 0; vertical-align: top; font-size: 11px; }
  th { text-align: left; font-weight: 600; border-bottom: 1px solid #000; }
  .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .big { font-size: 14px; font-weight: 700; }
  .mod { padding-left: 3mm; font-size: 10px; color: #222; }
  .qr { display: block; margin: 3mm auto 1mm; }
  .tiny { font-size: 9px; }
`;

/**
 * Render an HTML fragment into the hidden frame and print it.
 *
 * Returns false when the frame is missing (a non-browser environment or a
 * stripped host page) so the caller can tell the user instead of silently
 * doing nothing.
 */
export function printHtml(bodyHtml: string, title: string): boolean {
  if (typeof document === 'undefined') return false;
  const frame = document.getElementById(FRAME_ID);
  if (!(frame instanceof HTMLIFrameElement)) return false;
  const win = frame.contentWindow;
  const doc = frame.contentDocument;
  if (win === null || doc === null) return false;

  doc.open();
  doc.write(
    `<!doctype html><html lang="vi"><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>` +
      `<style>${BASE_STYLE}</style></head><body>${bodyHtml}</body></html>`,
  );
  doc.close();

  // One frame of delay so layout and any inline SVG have settled; printing an
  // unlaid-out document is how you get a blank slip.
  window.setTimeout(() => {
    try {
      win.focus();
      win.print();
    } catch {
      /* a kiosk with printing disabled — nothing useful to do here */
    }
  }, 60);
  return true;
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Money for paper: no currency symbol column-shifting, just grouped digits. */
export function printMoney(amountMinor: number, exponent = 0): string {
  const major = amountMinor / 10 ** exponent;
  return new Intl.NumberFormat('vi-VN', {
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent,
  }).format(major);
}
